import { observable, action, computed, makeObservable } from 'mobx'
import { task, TaskGroup } from 'mobx-task'
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs'
import { find } from 'rxjs/operators'
import { ViewModelList } from 'infra/ViewModelList'
import { toStream } from 'utils/mobx-util'
import { ApprovedTransactionViewModel } from './ApprovedTransactionViewModel'
import { ApprovedTransaction } from '../ApprovedTransaction'
import PendingTransaction from '../PendingTransaction'
import { AlertPort } from '../ports'
import { DraftPayout } from '../DraftPayout'
import { PayoutReviewStore } from '../PayoutReviewStore'
import { ConfirmSubmitModalViewModel } from './ConfirmSubmitModalViewModel'
import { AlertState } from '../../../alerts/Alert'
import { PreconditionFailed } from 'fejl'
import { csvDownload } from '../../../utils/csv-util'
import { serializeUserTransactionReason } from '../../UserTransactionReason'
import { serializeTransactionFlag } from '../../TransactionFlag'

/**
 * View model for drafting and submitting a payout.
 */
export class PayoutDraftingViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject(false)

  /**
   * Emits when payout completes.
   */
  private readonly _payoutCompleted = new Subject()

  /**
   * The approved transaction view models.
   */
  private readonly approvedTransactionVMs: ViewModelList<
    ApprovedTransaction,
    ApprovedTransactionViewModel
  >

  /**
   * The current draft.
   */
  @observable draft = DraftPayout.none

  /**
   * Task group.
   */
  readonly tasks: TaskGroup<any, any>

  /**
   * Emits when payout completes.
   */
  readonly payoutCompleted$ = this._payoutCompleted.asObservable()

  /**
   * Used to request submission confirmation.
   * On confirm, submits the payout.
   */
  readonly confirmSubmitVM = new ConfirmSubmitModalViewModel(
    () => this.submit(),
    this._active,
  )

  /**
   * Constructor.
   */
  constructor(
    private payoutReviewStore: PayoutReviewStore,
    private adapter: AlertPort,
  ) {
    makeObservable(this)
    this.tasks = TaskGroup<any, any>([
      this.approveTransactions,
      this.removeTransactions,
      this.submit,
    ])

    this.approvedTransactionVMs = new ViewModelList({
      models: () => this.draft.approvedTransactions.items.slice(),
      create: (model) =>
        new ApprovedTransactionViewModel(model, () =>
          this.removeTransactions([model]),
        ),
    })
  }

  /**
   * The approved transactions.
   */
  @computed
  get approvedTransactions() {
    return this.approvedTransactionVMs.items
  }

  /**
   * Whether we are able to submit.
   */
  @computed
  get canSubmit() {
    if (this.submit.pending) {
      return false
    }

    return this.approvedTransactions.length > 0
  }

  /**
   * The total approved amount.
   */
  @computed
  get total(): number {
    return this.draft.totalAmount
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._active.next(true)
    this.approvedTransactionVMs.activate()
    this.fetchCurrentDraft().then()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.approvedTransactionVMs.deactivate()
    this._active.next(false)
  }

  /**
   * Fetches the current draft.
   */
  fetchCurrentDraft = task(async () => {
    const draft = await this.payoutReviewStore.fetchCurrentDraftPayout()
    this.setDraft(draft)
  })

  /**
   * Approves the specified transactions.
   */
  approveTransactions = task.resolved(
    async (transactions: PendingTransaction[]) => {
      try {
        await this.waitForDraftToLoad()
        await this.payoutReviewStore.approveTransactions({
          transactions,
          draft: this.draft,
        })
      } catch (err: unknown) {
        this.adapter.newAlert().fromError(err).show()
      }
    },
  )

  /**
   * Removes the specified transactions.
   */
  removeTransactions = task.resolved(
    async (transactions: ApprovedTransaction[]) => {
      try {
        await this.waitForDraftToLoad()
        await this.payoutReviewStore.removeTransactions({
          transactions,
          draft: this.draft,
        })
      } catch (err: unknown) {
        this.adapter.newAlert().fromError(err).show()
      }
    },
  )

  /**
   * Removes all the approved transactions.
   */
  removeAll = () =>
    this.removeTransactions(this.approvedTransactions.map((v) => v.transaction))

  /**
   * Submit the payout.
   */
  submit = task.resolved(async () => {
    // NOTE: For now, we just poll the payout until it's
    // completed. In the future we will have a progress endpoint, and
    // an overview page that will track it. When we do, we'll redirect there
    // once we have submitted the draft.
    const draft = this.draft
    const alert = this.adapter
      .newAlert()
      .saying('Submitting', 'The payout is being submitted...')
      .withState(AlertState.Pending)
      .show()

    try {
      await this.waitForDraftToLoad()
      await this.payoutReviewStore.submitPayout({ draft })
    } catch (err) {
      if (err instanceof PreconditionFailed) {
        // There was a conflict; load the latest version of the draft.
        await this.fetchCurrentDraft()
        alert
          .withState(AlertState.Warning)
          .saying(
            'Wait a second!',
            'It looks like your copy of the draft was not entirely synchronized. It has been reloaded. Please verify everything looks correct before trying again.',
          )

        return
      }
      alert.fromError(err).autoDismiss()
      return
    }

    alert.saying(
      'Processing',
      'The payout was submitted and is being processed. No need to wait though.',
    )

    // Wait for the payout to complete.
    while (!(await this.payoutReviewStore.checkPayoutCompletion(draft.id))) {
      await new Promise((resolve) => setTimeout(resolve, 1_000))
    }

    // After the payout is submitted, the next draft payout is automatically
    // provisioned by the system which becomes the current draft payout.
    await this.fetchCurrentDraft()

    alert
      .saying('Payout completed!', 'The payout has completed!')
      .withState(AlertState.Success)
      .autoDismiss()

    this._payoutCompleted.next(undefined)
  })

  /**
   * Exports the approved transactions as CSV.
   */
  exportCsv = () => {
    const header = [
      'Job ID',
      'Transaction Short ID',
      'Recipient User ID',
      'Amount',
      'Workspace ID',
      'Date Created',
      'Date Transacted',
      'Date Approved',
      'Approved By User ID',
      'Reason',
      'Flags',
    ]

    const csvData = this.approvedTransactions.map((avm) => {
      const tx = avm.transaction
      return [
        tx.jobId ?? '',
        tx.shortId,
        tx.recipient.publicId,
        tx.amount.toString(),
        tx.workspace.id,
        tx.createdAt.toLocaleDateString(),
        tx.transactedAt.toLocaleDateString(),
        tx.approvedAt.toLocaleDateString(),
        tx.approvedBy.publicId,
        tx.reason ? serializeUserTransactionReason(tx.reason) : '',
        tx.flags.map(serializeTransactionFlag).join(', '),
      ]
    })

    csvDownload([header, ...csvData], 'approved-transactions.csv')
  }

  /**
   * Waits for the draft to have loaded.
   * This is called before operations that work with the draft.
   */
  private async waitForDraftToLoad() {
    await firstValueFrom(
      toStream(() => this.draft, true).pipe(
        find((draft) => draft !== DraftPayout.none),
      ),
      {
        defaultValue: null,
      },
    )
  }

  /**
   * Sets the draft.
   */
  @action.bound
  private setDraft(draft: DraftPayout) {
    this.draft = draft
  }
}
